import React, { useState } from 'react';
import { useQuizManagement } from '@/hooks/useQuizManagement';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Plus, BookOpen, Users, Trophy } from 'lucide-react';
import { QuizDialog } from './QuizDialog';
import { QuizTable } from './QuizTable';
import { QuizBuilder } from './QuizBuilder';
import { Quiz } from '@/types/quiz';

interface QuizManagementProps {
  programId: string;
  programTitle: string;
  onBack: () => void;
}

export const QuizManagement = ({ programId, programTitle, onBack }: QuizManagementProps) => {
  const { quizzes, loading, createQuiz, updateQuiz, deleteQuiz } = useQuizManagement(programId);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState<Quiz | null>(null);
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [showQuizBuilder, setShowQuizBuilder] = useState(false);

  const handleCreateQuiz = async (quizData: Omit<Quiz, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      await createQuiz({
        ...quizData,
        program_id: programId
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to create quiz:', error);
    }
  };

  const handleEditQuiz = (quiz: Quiz) => {
    setEditingQuiz(quiz);
    setIsDialogOpen(true);
  };

  const handleUpdateQuiz = async (quizData: Omit<Quiz, 'id' | 'created_at' | 'updated_at'>) => {
    if (!editingQuiz) return;
    
    try {
      await updateQuiz(editingQuiz.id, quizData);
      setIsDialogOpen(false);
      setEditingQuiz(null);
    } catch (error) {
      console.error('Failed to update quiz:', error);
    }
  };

  const handleDeleteQuiz = async (quizId: string) => {
    if (!confirm('Are you sure you want to delete this quiz? This will also delete all associated questions and attempts.')) {
      return;
    }

    try {
      await deleteQuiz(quizId);
    } catch (error) {
      console.error('Failed to delete quiz:', error);
    }
  };

  const handleManageQuestions = (quiz: Quiz) => {
    setSelectedQuiz(quiz);
    setShowQuizBuilder(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingQuiz(null);
  };

  if (showQuizBuilder && selectedQuiz) {
    return (
      <QuizBuilder
        quiz={selectedQuiz}
        onBack={() => {
          setShowQuizBuilder(false);
          setSelectedQuiz(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Programs
          </Button>
          <div>
            <h3 className="text-lg font-semibold">Quiz Management</h3>
            <p className="text-sm text-muted-foreground">{programTitle}</p>
          </div>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Quiz
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Program Quizzes
          </CardTitle>
          <CardDescription>
            Create and manage quizzes to test player knowledge and award points
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Loading quizzes...</div>
          ) : quizzes.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h4 className="text-lg font-semibold mb-2">No Quizzes</h4>
              <p className="text-muted-foreground mb-4">
                Create quizzes to test player knowledge and add gamification to your program.
              </p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Quiz
              </Button>
            </div>
          ) : (
            <QuizTable
              quizzes={quizzes}
              onEdit={handleEditQuiz}
              onDelete={handleDeleteQuiz}
              onManageQuestions={handleManageQuestions}
            />
          )}
        </CardContent>
      </Card>

      <QuizDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={editingQuiz ? handleUpdateQuiz : handleCreateQuiz}
        quiz={editingQuiz}
      />
    </div>
  );
};